import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Loading } from 'react-daisyui';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import useGovernanceCheck from '@/api/useGovernanceCheck.ts';
import useGovernanceCompassPreview from '@/api/useGovernanceCompassPreview.ts';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import StepFormSubtitle from '@/components/ui/StepFormSubtitle/StepFormSubtitle.tsx';
import StepFormTitle from '@/components/ui/StepFormTitle/StepFormTitle.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { buildGovernanceCheckRequest } from '@/pages/recommendationEngine/buildGovernanceCheckRequest.ts';
import type {
  RecommendationEngineData,
  RecommendationEngineSteps,
  SessionCompletionRoute,
} from '@/pages/recommendationEngine/recommendationEngineContext.ts';
import type {
  CompassFilter,
  ConfidenceThresholdResult,
  GovernanceCheckResponse,
  GovernanceStatus,
} from '$/requests/GovernanceCheck.ts';
import {
  compassThemeForFilterStatus,
  compassThemeForGovernanceStatus,
} from '@/pages/recommendationEngine/compassReviewTheme.ts';
import RiskFlagsList from '@/components/ui/RiskFlagsList/RiskFlagsList.tsx';
import { formatGovScore } from '@/utils/sessionTableDisplay.ts';
import { cn } from '@/utils/classNames.ts';

/** Step 4 — COMPASS™ review (Story 8): compass preview on mount; ledger commit on Complete Session. */
const StepCompassReview: FC = () => {
  const { t } = useTranslation();
  const { goToStep, previousStep, setContextData, contextData } = useStepperContext<
    RecommendationEngineSteps,
    RecommendationEngineData
  >();
  const d = contextData as Partial<RecommendationEngineData> | null;
  const { mutate: runGovernanceCheck, isPending: isCommitPending } = useGovernanceCheck();
  const [commitError, setCommitError] = useState<string | null>(null);

  const previewRequest = useMemo(() => {
    try {
      if (!d) return null;
      return buildGovernanceCheckRequest(d);
    } catch {
      return null;
    }
  }, [d]);

  const result = d?.governance_result;
  const shouldLoadPreview = !result && Boolean(previewRequest);

  const {
    data: compassPreviewData,
    isPending: isPreviewPending,
    isFetching: isPreviewFetching,
    isError: isPreviewError,
    error: previewQueryError,
    refetch: refetchCompassPreview,
  } = useGovernanceCompassPreview(previewRequest, { enabled: shouldLoadPreview });

  const loadError = isPreviewError
    ? previewQueryError instanceof Error
      ? previewQueryError.message
      : t('recommendationEnginePage.compass.loadFailed')
    : null;

  const isLoading = shouldLoadPreview && (isPreviewPending || isPreviewFetching) && !compassPreviewData;

  useEffect(() => {
    if (!compassPreviewData || result) return;
    setContextData((prev) => ({ ...prev, governance_result: compassPreviewData }));
  }, [compassPreviewData, result, setContextData]);

  const sealGovernance = (route: SessionCompletionRoute, onSealed: () => void) => {
    if (!d) return;
    setCommitError(null);

    if (d.governance_sealed) {
      setContextData((prev) => ({ ...prev, completion_route: route }));
      onSealed();
      return;
    }

    try {
      const body = buildGovernanceCheckRequest(d);
      runGovernanceCheck(body, {
        onSuccess: (response) => {
          setContextData((prev) => ({
            ...prev,
            governance_sealed: true,
            governance_result: response,
            ledger_commit: response.ledger_commit,
            session_outcome: response.ledger_commit ? 'ledger_success' : prev?.session_outcome,
            completion_route: route,
          }));
          onSealed();
        },
        onError: (err) => {
          setCommitError(
            err instanceof Error ? err.message : t('recommendationEnginePage.compass.commitFailed'),
          );
        },
      });
    } catch (err) {
      setCommitError(
        err instanceof Error ? err.message : t('recommendationEnginePage.compass.commitFailed'),
      );
    }
  };

  const handleCompleteSession = () => {
    sealGovernance('complete', () => goToStep('session-recorded'));
  };

  const handleProceedWithCaution = () => {
    sealGovernance('caution', () => goToStep('session-recorded'));
  };

  const handleRequestHumanReview = () => {
    setContextData((prev) => ({ ...prev, completion_route: 'escalation' }));
    goToStep('fiduciary-escalation');
  };

  return (
    <BrandCardSideContent>
      <div className="flex flex-col flex-1 gap-6 w-full min-h-0">
        <div className="flex flex-col gap-2 shrink-0">
          <StepFormTitle>{t('recommendationEnginePage.compass.title')}</StepFormTitle>
          <StepFormSubtitle>{t('recommendationEnginePage.compass.subtitle')}</StepFormSubtitle>
        </div>

        {loadError && (
          <Alert status="error" className="shrink-0">
            <span>{loadError}</span>
            <Button
              size="sm"
              color="ghost"
              className="ml-2"
              disabled={isLoading}
              onClick={() => void refetchCompassPreview()}
            >
              {t('button.tryAgain')}
            </Button>
          </Alert>
        )}

        {isLoading && !loadError && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12 min-h-[240px]">
            <Loading size="lg" className="text-[#0F1B2A]" />
            <p className="font-dm text-sm font-semibold text-black/70 text-center">
              {t('recommendationEnginePage.compass.evaluating')}
            </p>
          </div>
        )}

        {commitError && (
          <Alert status="error" className="shrink-0">
            <span>{commitError}</span>
          </Alert>
        )}

        {result && (
          <>
            <CompassMetricsPanel result={result} />
            <GovernanceStatusBanner status={result.governance_status} />
            <GovernanceHelperCopy status={result.governance_status} customCopy={result.governance_helper_copy} />
            {result.confidence_threshold_result && (
              <ConfidenceThresholdPanel threshold={result.confidence_threshold_result} />
            )}
            <div className="divider my-auto" />
            <div className="flex flex-col gap-3 min-h-0 overflow-y-auto flex-1 pr-0.5">
              <p className="font-newsreader font-extrabold text-xl tracking-wide text-black/80 uppercase">
                {t('recommendationEnginePage.compass.flaggedItems')}
              </p>
              {result.risk_flags && result.risk_flags.length > 0 && (
                <RiskFlagsList flags={result.risk_flags} />
              )}
              {result.compass_filters.map((filter) => (
                <CompassFilterCard key={filter.name} filter={filter} />
              ))}
            </div>

            <CompassActionArea
              status={result.governance_status}
              isCommitting={isCommitPending}
              onCompleteSession={handleCompleteSession}
              onProceedWithCaution={handleProceedWithCaution}
              onRequestHumanReview={handleRequestHumanReview}
              onRefineIssue={() => goToStep('governance-review')}
              onRefineCase={() => goToStep('commitment-checkpoint')}
              onBack={previousStep}
            />
          </>
        )}
      </div>
    </BrandCardSideContent>
  );
};

export default StepCompassReview;

const LIFECYCLE_LABEL_KEYS: Record<string, string> = {
  intent_captured: 'recommendationEnginePage.compass.lifecycle.intent_captured',
  framework_routed: 'recommendationEnginePage.compass.lifecycle.framework_routed',
  governance_evaluated: 'recommendationEnginePage.compass.lifecycle.governance_evaluated',
  escalated: 'recommendationEnginePage.compass.lifecycle.escalated',
  execution_asset_produced: 'recommendationEnginePage.compass.lifecycle.execution_asset_produced',
};

function CompassMetricsPanel({ result }: { result: GovernanceCheckResponse }) {
  const { t } = useTranslation();
  const lifecycleKey = result.lifecycle_status
    ? LIFECYCLE_LABEL_KEYS[result.lifecycle_status]
    : undefined;
  const lifecycleLabel = lifecycleKey
    ? t(lifecycleKey)
    : t('recommendationEnginePage.compass.lifecycle.framework_routed');

  return (
    <div className="flex flex-wrap gap-4 px-4 py-3 rounded-xl border border-[rgba(0,0,0,0.08)] bg-[#f4f9ff] shrink-0">
      <div className="flex flex-col gap-0.5">
        <span className="font-dm text-[10px] font-bold uppercase tracking-wide text-black/45">
          {t('recommendationEnginePage.compass.governanceScore')}
        </span>
        <span className="font-dm text-lg font-bold text-[#0F1B2A]">
          {formatGovScore(result.governance_score)}
        </span>
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="font-dm text-[10px] font-bold uppercase tracking-wide text-black/45">
          {t('recommendationEnginePage.compass.lifecycleStatus')}
        </span>
        <span className="font-dm text-sm font-semibold text-black/70">{lifecycleLabel}</span>
      </div>
    </div>
  );
}

function ConfidenceThresholdPanel({ threshold }: { threshold: ConfidenceThresholdResult }) {
  const { t } = useTranslation();
  const scoreDisplay = (threshold.governance_score * 10).toFixed(1);
  const minDisplay = (threshold.governance_score_threshold * 10).toFixed(1);

  return (
    <div className="rounded-xl border border-[rgba(0,0,0,0.08)] bg-white px-4 py-3 flex flex-col gap-1 shrink-0">
      <p className="font-dm text-xs font-bold text-black/55 uppercase tracking-wide">
        {t('recommendationEnginePage.compass.thresholdTitle')}
      </p>
      <p className="font-dm text-sm text-black/70">
        {threshold.score_threshold_passed
          ? t('recommendationEnginePage.compass.thresholdPassed', { score: scoreDisplay, min: minDisplay })
          : t('recommendationEnginePage.compass.thresholdFailed', { score: scoreDisplay, min: minDisplay })}
      </p>
      <p className="font-dm text-xs text-black/50">
        {t('recommendationEnginePage.compass.executiveConfidence', {
          level: threshold.executive_commitment_confidence,
        })}
      </p>
      {threshold.human_in_loop_required && (
        <p className="font-dm text-xs font-semibold text-[#b38600]">
          {t('recommendationEnginePage.compass.humanInLoop')}
        </p>
      )}
    </div>
  );
}

function GovernanceStatusBanner({ status }: { status: GovernanceStatus }) {
  const { t } = useTranslation();
  const theme = compassThemeForGovernanceStatus(status);

  const icons = {
    pass: CheckCircleIcon,
    caution: InformationCircleIcon,
    escalation: ExclamationCircleIcon,
  } as const;

  const Icon = icons[status];
  const title = t(`recommendationEnginePage.compass.status.${status}.title`);
  const detail = t(`recommendationEnginePage.compass.status.${status}.detail`);

  return (
    <div
      className={cn(
        'rounded-xl px-4 py-3 flex flex-col gap-1 shrink-0 border',
        theme.surface,
        theme.text,
        theme.border,
      )}
    >
      <div className="flex flex-row items-center gap-2">
        <Icon className="w-5 h-5 shrink-0" aria-hidden />
        <span className="font-dm text-sm font-semibold">{title}</span>
      </div>
      <p className="font-dm text-sm pl-7 opacity-90">{detail}</p>
    </div>
  );
}

function GovernanceHelperCopy({
  status,
  customCopy,
}: {
  status: GovernanceStatus;
  customCopy?: string;
}) {
  const { t } = useTranslation();

  if (status === 'pass' && customCopy) {
    return (
      <p className="font-dm text-xs italic text-black/50 text-left px-1 shrink-0">{customCopy}</p>
    );
  }

  if (status === 'caution') {
    return (
      <p className="font-dm text-xs italic text-black/50 text-left px-1 shrink-0">
        {customCopy ?? t('recommendationEnginePage.compass.helper.caution')}
      </p>
    );
  }

  if (status === 'escalation') {
    return (
      <p className="font-dm text-xs italic text-black/50 text-left px-1 shrink-0">
        {customCopy ?? t('recommendationEnginePage.compass.helper.escalation')}
      </p>
    );
  }

  return null;
}

function CompassFilterCard({ filter }: { filter: CompassFilter }) {
  const { t } = useTranslation();
  const statusLabel = t(`recommendationEnginePage.compass.filterStatus.${filter.status}`);
  const theme = compassThemeForFilterStatus(filter.status);

  return (
    <div
      className={cn(
        'rounded-2xl border p-4 flex flex-col gap-2 font-dm text-sm',
        theme.surface,
        theme.text,
        theme.border,
      )}
    >
      <p className="font-semibold">
        {filter.name} – {statusLabel}
      </p>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-80">
        {t('recommendationEnginePage.compass.frameworkConnection')}
      </p>
      {filter.framework_connection && (
        <p className="leading-relaxed opacity-95">{filter.framework_connection}</p>
      )}
      {filter.explanation && <p className="leading-relaxed opacity-90">{filter.explanation}</p>}
      {filter.next_session_recommendation && (
        <p className="leading-relaxed italic opacity-85">{filter.next_session_recommendation}</p>
      )}
    </div>
  );
}

function CompassActionArea({
  status,
  isCommitting,
  onCompleteSession,
  onProceedWithCaution,
  onRequestHumanReview,
  onRefineIssue,
  onRefineCase,
  onBack,
}: {
  status: GovernanceStatus;
  isCommitting: boolean;
  onCompleteSession: () => void;
  onProceedWithCaution: () => void;
  onRequestHumanReview: () => void;
  onRefineIssue: () => void;
  onRefineCase: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();

  if (status === 'pass') {
    return (
      <div className="flex flex-col gap-2 shrink-0 mt-auto">
        <LoadingButton loading={isCommitting} color="primary" className="w-full" type="button" onClick={onCompleteSession}>
          {t('recommendationEnginePage.compass.cta.completeSession')}
        </LoadingButton>
        <button
          type="button"
          disabled={isCommitting}
          className="w-full py-2 px-[22px] h-[42px] bg-white border border-[rgba(0,0,0,0.12)] rounded-xl font-dm font-bold text-sm text-[#0F1B2A]"
          onClick={onBack}
        >
          {t('button.goBack')}
        </button>
      </div>
    );
  }

  if (status === 'caution') {
    return (
      <div className="flex flex-col gap-2 shrink-0 mt-auto">
        <LoadingButton loading={isCommitting} color="primary" className="w-full" type="button" onClick={onProceedWithCaution}>
          {t('recommendationEnginePage.compass.cta.proceedWithCaution')}
        </LoadingButton>
        <button
          type="button"
          disabled={isCommitting}
          className="w-full py-3 px-4 rounded-xl font-dm font-bold text-sm border border-[rgba(0,0,0,0.12)] bg-white text-[#0F1B2A]"
          onClick={onRefineIssue}
        >
          {t('recommendationEnginePage.compass.cta.refineIssue')}
        </button>
        <button
          type="button"
          disabled={isCommitting}
          className="w-full py-2 text-sm font-dm font-bold text-[#0F1B2A] opacity-70"
          onClick={onBack}
        >
          {t('button.goBack')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 shrink-0 mt-auto">
      <LoadingButton
        loading={isCommitting}
        color="primary"
        className="w-full"
        type="button"
        onClick={onRequestHumanReview}
      >
        {t('recommendationEnginePage.compass.cta.requestHumanReview')}
      </LoadingButton>
      <button
        type="button"
        className="w-full py-3 px-4 rounded-xl font-dm font-bold text-sm border border-[rgba(0,0,0,0.12)] bg-white text-[#0F1B2A]"
        onClick={onRefineCase}
      >
        {t('recommendationEnginePage.compass.cta.refineCase')}
      </button>
      <button
        type="button"
        className="w-full py-2 text-sm font-dm font-bold text-[#0F1B2A] opacity-70"
        onClick={onBack}
      >
        {t('button.goBack')}
      </button>
    </div>
  );
}
